/**
 * React Query hooks for the businesses module.
 *
 * Auth gating:
 *   Both queries are gated on `!!token` from useAuthStore. Before login
 *   (fresh browser or after logout) they don't fire, killing the "400
 *   Authentication token required" flood that happened when the
 *   portfolio page mounted before the user had logged in.
 *
 *   Callers can still pass `enabled: false` to disable a query; the
 *   internal gate ANDs with the caller's flag.
 *
 * Cache strategy:
 *   - useMyBusinesses: long stale time (5 min). The list of businesses
 *     a user can access changes rarely (only when admin adds/removes
 *     a business). No need to refetch on every mount.
 *
 *   - usePortfolioToday: short stale time (30 sec). Today's metrics
 *     are dynamic — every order, every expenditure changes them. We
 *     could go shorter, but 30 sec balances freshness vs request rate.
 *     The portfolio page also has a refresh button for explicit updates.
 *
 * Query keys are tuple-style so React Query can invalidate by prefix
 * (e.g. invalidate ['businesses'] to refresh both queries after a
 * business is added).
 */
import { useQuery } from "@tanstack/react-query";
import useAuthStore from "@/stores/authStore";
import businessService from "@/services/businessService";
import type { Business, PortfolioToday } from "@/types/business.types";

// ─── Query keys ─────────────────────────────────────────────────────
export const businessKeys = {
  all: ["businesses"] as const,
  mine: () => [...businessKeys.all, "mine"] as const,
  portfolio: () => [...businessKeys.all, "portfolio"] as const,
  portfolioToday: () => [...businessKeys.portfolio(), "today"] as const,
};

// ─── Auth gate helper ───────────────────────────────────────────────
function useAuthGate(callerEnabled: boolean | undefined): boolean {
  const token = useAuthStore((s) => s.token);
  const baseEnabled = callerEnabled ?? true;
  return baseEnabled && !!token;
}

// ─── Hooks ──────────────────────────────────────────────────────────

/**
 * List of businesses the current user can access.
 *
 * Stable list — stale time 5 minutes, garbage collection at 10 min.
 */
export function useMyBusinesses(options?: { enabled?: boolean }) {
  const enabled = useAuthGate(options?.enabled);
  return useQuery<Business[]>({
    queryKey: businessKeys.mine(),
    queryFn: () => businessService.getMyBusinesses(),
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 10 * 60 * 1000, // 10 min
    enabled,
  });
}

/**
 * Today's portfolio metrics for tenant-wide users.
 *
 * Dynamic data — stale at 30 sec. Refetches on window focus so a
 * tenant returning to the tab sees fresh numbers without manual
 * action.
 */
export function usePortfolioToday(options?: { enabled?: boolean }) {
  const enabled = useAuthGate(options?.enabled);
  return useQuery<PortfolioToday>({
    queryKey: businessKeys.portfolioToday(),
    queryFn: () => businessService.getPortfolioToday(),
    staleTime: 30 * 1000, // 30 sec
    refetchOnWindowFocus: true,
    enabled,
  });
}
